// Граф захвата: микрофон → worklet → gain(0) → выход контекста.
//
// Контекст создаётся отдельно и **синхронно**, внутри обработчика клика: всё,
// что требует звука, обязано успеть до первого `await`, иначе транзиентная
// активация истечёт и контекст останется `suspended`, а `process()` не
// вызовется ни разу.

import { captureConstraints } from "./devices.ts";

/** Частота при включённой экономии канала. Uplink втрое меньше. */
export const THRIFTY_RATE = 16_000;
export const DEFAULT_RATE = 48_000;

/**
 * Размер сетевого блока в сэмплах — всегда 10 мс.
 *
 * Считается от фактической частоты захвата, а не берётся числом: 480 сэмплов
 * при 48 кГц и 160 при 16 кГц — это одни и те же 10 мс, а 480 при 16 кГц уже
 * 30 мс, то есть втрое более грубая нарезка и втрое более рваная отправка.
 * Десять миллисекунд выбраны не случайно — это кадр RNNoise, и при
 * невыровненном блоке шумодав копит остаток.
 */
export function blockSizeFor(sampleRate: number): number {
  return Math.max(1, Math.round(sampleRate / 100));
}

export interface CaptureContext {
  context: AudioContext;
  /** Частота, которую браузер дал на самом деле. Именно её шлём в хендшейке. */
  requestedRate: number;
  /** Браузер отказал в запрошенной частоте. Стоит сказать об этом вслух. */
  rateRefused: boolean;
}

/**
 * Создать контекст захвата. Синхронно — вызывать в обработчике жеста.
 *
 * `sampleRate: 16000` браузер вправе не поддержать. Отката на молчаливое «как
 * получится» тут быть не может: в хендшейк уезжает фактическая частота
 * контекста, а не запрошенная, иначе сервер построит пайплайн под одну частоту,
 * а получит другую, и звук растянется.
 */
export function createCaptureContext(thrifty: boolean): CaptureContext {
  const wanted = thrifty ? THRIFTY_RATE : DEFAULT_RATE;
  try {
    return {
      context: new AudioContext({ sampleRate: wanted, latencyHint: "interactive" }),
      requestedRate: wanted,
      rateRefused: false,
    };
  } catch {
    return {
      context: new AudioContext({ latencyHint: "interactive" }),
      requestedRate: wanted,
      rateRefused: true,
    };
  }
}

export interface Capture {
  stream: MediaStream;
  node: AudioWorkletNode;
  track: MediaStreamTrack;
  stop(): void;
}

export interface CaptureOptions {
  context: AudioContext;
  deviceId: string | null;
  blockSize: number;
  buildTag: string;
  /** Микрофон пропал или разрешение отозвали. */
  onLost(): void;
}

/** Открыть микрофон и собрать граф. */
export async function startCapture(options: CaptureOptions): Promise<Capture> {
  const { context, deviceId, blockSize } = options;

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia(captureConstraints(deviceId));
  } catch (error) {
    // Сохранённое устройство могло исчезнуть между запусками. Просить его
    // `exact` и падать — хуже, чем взять любое и сказать об этом.
    if (deviceId !== null && error instanceof DOMException && error.name === "OverconstrainedError") {
      stream = await navigator.mediaDevices.getUserMedia(captureConstraints(null));
    } else {
      throw error;
    }
  }

  // С этого места устройство уже открыто, и упасть здесь значит оставить его
  // занятым: индикатор записи горит, микрофон недоступен другим вкладкам, и
  // держится это до перезагрузки страницы. Ссылки на поток наружу ещё нет, так
  // что убрать за собой можем только мы.
  //
  // Случай не выдуманный. Запуск ограничен пределом ожидания, по истечении
  // которого вызывающая сторона закрывает аудиоконтекст, — а `getUserMedia` к
  // тому времени вполне может ещё висеть на запросе разрешения, от которого
  // человек отошёл. Он возвращается, нажимает «Разрешить», поток открывается,
  // и следующая же строка падает на закрытом контексте.
  let source: MediaStreamAudioSourceNode;
  let node: AudioWorkletNode;
  let silent: GainNode;
  try {
    await context.audioWorklet.addModule(`capture-processor.js?v=${options.buildTag}`);

    source = context.createMediaStreamSource(stream);
    node = new AudioWorkletNode(context, "capture", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: { blockSize },
    });

    // Ноль не ради тишины, а ради того, чтобы граф «тянул» узел: поведение узла
    // без подключённого выхода в браузерах различается, и там, где его не тянут,
    // process() просто не вызывается — счётчик отправленных блоков стоит на нуле.
    silent = context.createGain();
    silent.gain.value = 0;

    source.connect(node).connect(silent).connect(context.destination);
  } catch (error) {
    for (const each of stream.getTracks()) {
      each.stop();
    }
    throw error;
  }

  const track = stream.getAudioTracks()[0]!;
  track.addEventListener("ended", options.onLost);

  return {
    stream,
    node,
    track,
    stop() {
      track.removeEventListener("ended", options.onLost);
      source.disconnect();
      node.disconnect();
      silent.disconnect();
      for (const each of stream.getTracks()) {
        each.stop();
      }
    },
  };
}
